import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLanguage, LangSwitcher } from "@/context/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEMO_USERS = [
    { email: "jeff.chapleau@gmail.com", password: "AppTrack2026!", name: "Jeff Chapleau", role: "Administrator", org: "City of Brampton", color: "#0A2540" },
    { email: "sarah.chen@brookfield.ca", password: "Planner2026!", name: "Sarah Chen", role: "Senior Planner", org: "Planning Services", color: "#00B4D8" },
    { email: "james.odeyemi@brookfield.ca", password: "Planner2026!", name: "James Odeyemi", role: "Planner", org: "Planning Services", color: "#4F5DD5" },
    { email: "priya.nair@brampton.ca", password: "Inspector2026!", name: "Priya Nair", role: "Field Inspector", org: "Building Services", color: "#06D6A0" },
];

const BG = "https://images.unsplash.com/photo-1776218643389-98159ee68366?q=80&w=1920&auto=format&fit=crop";

export default function Login() {
    const { login } = useAuth();
    const { t, tr } = useLanguage();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [entraOn, setEntraOn] = useState(false);

    useEffect(() => {
        axios.get(`${API}/auth/entra/status`).then((r) => setEntraOn(!!r.data?.configured)).catch(() => {});
    }, []);

    const pickDemo = (u) => {
        setSelectedUser(u.email);
        setEmail(u.email);
        setPassword(u.password);
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!email || !password || busy) return;
        setBusy(true);
        try {
            const user = await login(email, password);
            toast.success(`Welcome back, ${user.name.split(" ")[0]}`);
            navigate(user.role === "developer" ? "/developer" : user.role === "inspector" ? "/field" : "/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sign in failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen relative flex items-center justify-center overflow-hidden" data-testid="login-page">
            <div className="absolute inset-0 bg-cover bg-center scale-105" style={{ backgroundImage: `url(${BG})`, filter: "blur(6px) brightness(0.55) saturate(0.9)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, rgba(10,37,64,0.75) 0%, rgba(10,37,64,0.35) 60%, rgba(0,180,216,0.25) 100%)" }} />

            <div className="absolute top-6 right-6 z-10"><LangSwitcher dark testid="login-lang-switcher" /></div>
            <Link to="/" data-testid="login-back-home" className="absolute top-6 left-6 z-10 inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" /> {t("common.back")}
            </Link>

            <motion.div initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}
                className="relative z-10 w-full max-w-md mx-4">
                <div className="glass-dark rounded-[20px] p-8 sm:p-10 shadow-2xl border border-white/15" style={{ backdropFilter: "blur(28px)" }}>
                    <div className="flex flex-col items-center text-center">
                        <img src="/assets/logo-light.png" alt="LandLogic NexusOne" className="w-28 h-auto drop-shadow-2xl" />
                        <p className="text-sm text-white/60 mt-2">{t("login.staff_sub")}</p>
                    </div>

                    <div className="mt-7">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/50 mb-3 text-center">{t("login.demo_title")}</div>
                        <div className="grid grid-cols-3 gap-3">
                            {DEMO_USERS.map((u) => (
                                <button key={u.email} data-testid={`demo-login-${u.role.toLowerCase().replace(/\s+/g, "-")}`}
                                    onClick={() => pickDemo(u)}
                                    className={`group flex flex-col items-center gap-2 p-3 rounded-[14px] border transition-all duration-200 ${
                                        selectedUser === u.email ? "border-[#00B4D8] bg-[#00B4D8]/15 scale-[1.03]" : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/25"
                                    }`}>
                                    <div className="relative">
                                        <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-heading font-semibold text-sm ring-2 ring-white/20 group-hover:ring-white/40 transition-all"
                                            style={{ backgroundColor: u.color }}>
                                            {u.name.split(" ").map((p) => p[0]).join("")}
                                        </div>
                                        {selectedUser === u.email && (
                                            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#06D6A0] border-2 border-[#0d2136] flex items-center justify-center">
                                                <Lock className="w-2 h-2 text-white" />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <div className="text-[12px] font-medium text-white leading-tight">{u.name.split(" ")[0]}</div>
                                        <div className="text-[10px] text-white/50 leading-tight mt-0.5">{tr(u.role)}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <form onSubmit={submit} className="mt-7 space-y-3.5">
                        <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("login.email")} autoComplete="email"
                            className="w-full rounded-[10px] bg-white/10 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition-all" />
                        <div className="relative">
                            <input data-testid="login-password-input" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder={t("login.password")} autoComplete="current-password"
                                className="w-full rounded-[10px] bg-white/10 border border-white/15 px-4 py-3 pr-11 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition-all" />
                            <button type="button" data-testid="login-toggle-password" onClick={() => setShowPw(!showPw)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors">
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        <button type="submit" data-testid="login-submit-btn" disabled={busy || !email || !password}
                            className="w-full py-3.5 rounded-[10px] bg-[#00B4D8] hover:bg-[#00c3eb] text-white font-semibold text-sm transition-all duration-200 hover:shadow-lg hover:shadow-[#00B4D8]/30 active:scale-[0.98] disabled:opacity-40 inline-flex items-center justify-center gap-2">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                            {t("common.signin")}
                        </button>
                    </form>

                    {entraOn && (
                        <>
                            <div className="my-4 flex items-center gap-3">
                                <div className="flex-1 h-px bg-white/10" />
                                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">{tr("or")}</span>
                                <div className="flex-1 h-px bg-white/10" />
                            </div>
                            <button type="button" data-testid="login-entra-btn"
                                onClick={() => { window.location.href = `${API}/auth/entra/login`; }}
                                className="w-full py-3 rounded-[10px] bg-white/10 border border-white/15 text-white font-semibold text-sm transition-all hover:bg-white/15 active:scale-[0.98] inline-flex items-center justify-center gap-2.5">
                                <svg viewBox="0 0 23 23" className="w-4 h-4"><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
                                {tr("Sign in with Microsoft")}
                            </button>
                            <p className="mt-2 text-center text-[10px] text-white/35">{tr("City staff — use your municipal account")}</p>
                        </>
                    )}

                    <p className="mt-6 text-center text-[11px] text-white/40 leading-relaxed">
                        {t("login.no_account")}
                    </p>
                </div>
            </motion.div>
        </div>
    );
}
